import { useState, useCallback, useEffect, useRef } from 'react';
import {
  WaitingState,
  WaitingConfig,
  WaitingContext,
  WaitingType,
  ReelItem,
  WaitingSessionMetadata
} from '../types';
import waitingService from '../services/waitingService';

interface UseWaitingStateProps {
  brandId: string;
  waitingType: WaitingType;
  productSku?: string;
  userId?: string;
  checkResultReady: () => Promise<boolean>;
  onResultReady?: () => void;
}

interface UseWaitingStateReturn extends WaitingContext {
  startSession: () => Promise<void>;
  handleContinueClick: () => void;
  nextReel: () => void;
  prevReel: () => void;
  trackEvent: (eventName: string, properties?: Record<string, unknown>) => void;
  isLoading: boolean;
  error: string | null;
  sessionMetadata?: WaitingSessionMetadata;
}

export function useWaitingState({
  brandId,
  waitingType,
  productSku,
  userId,
  checkResultReady,
  onResultReady
}: UseWaitingStateProps): UseWaitingStateReturn {
  const [state, setState] = useState<WaitingState>('GENERATING');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [config, setConfig] = useState<WaitingConfig | null>(null);
  const [configId, setConfigId] = useState<string | null>(null);
  const [progressPercent, setProgressPercent] = useState(0);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [isResultReady, setIsResultReady] = useState(false);
  const [reels, setReels] = useState<ReelItem[]>([]);
  const [currentReelIndex, setCurrentReelIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sessionMetadata, setSessionMetadata] = useState<WaitingSessionMetadata | undefined>(undefined);

  const startTimeRef = useRef<number>(Date.now());
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const softGateTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const resultReadyCalledRef = useRef(false);

  const trackEvent = useCallback(
    async (eventName: string, properties: Record<string, unknown> = {}) => {
      if (!sessionId) return;
      
      try {
        await waitingService.trackEvent({
          sessionId,
          eventName: eventName as any,
          properties
        });
      } catch (err) {
        console.error('Failed to track event:', err);
      }
    },
    [sessionId]
  );

  const loadContent = useCallback(async () => {
    if (!config?.modules) return;

    try {
      // Determine reel source based on config
      const hasUserReels = config.modules.reels;
      const hasBrandReels = config.modules.brand_reels;
      
      if (hasUserReels || hasBrandReels) {
        let source: 'user' | 'brand' | 'mixed' = 'user';
        
        if (hasUserReels && hasBrandReels) {
          source = 'mixed';
        } else if (hasBrandReels) {
          source = 'brand';
        } else {
          source = 'user';
        }
        
        const reelsData = await waitingService.getReels(brandId, productSku, 20, source);
        setReels(reelsData);
      }    } catch (err) {
      console.error('Failed to load waiting content:', err);
    }
  }, [brandId, productSku, config]);

  const startSession = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const { configId: fetchedConfigId, config: fetchedConfig } = 
        await waitingService.getConfig(brandId, waitingType);
      
      setConfig(fetchedConfig);
      setConfigId(fetchedConfigId);

      const session = await waitingService.createSession({
        brandId,
        userId,
        waitingType,
        configId: fetchedConfigId || undefined,
        metadata: { productSku }
      });

      setSessionId(session.id);
      setSessionMetadata(session.metadata);
      startTimeRef.current = Date.now();
      setState('GENERATING');
      window.parent.postMessage({ type: 'GENERATION_STARTED' }, '*');
      setIsLoading(false);

      // Track waiting_started event
      try {
        await waitingService.trackEvent({
          sessionId: session.id,
          eventName: 'waiting_started',
          properties: { waitingType, productSku }
        });
      } catch (trackErr) {
        console.error('Failed to track waiting_started:', trackErr);
      }

    } catch (err) {
      console.error('Failed to start waiting session:', err);
      setError('Failed to initialize waiting screen');
      window.parent.postMessage({ type: 'GENERATION_FAILED' }, '*');
      setIsLoading(false);
    }
  }, [brandId, waitingType, productSku, userId]);

  useEffect(() => {
    if (config) {
      loadContent();
    }
  }, [config, loadContent]);

  useEffect(() => {
    if (!sessionId || !config) return;

    const estimatedDuration = config.estimated_duration_seconds;
    
    progressIntervalRef.current = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
      setElapsedSeconds(elapsed);

      let progress: number;
      if (isResultReady) {
        progress = 100;
      } else {
        progress = Math.min(95, (elapsed / estimatedDuration) * 100);
      }
      setProgressPercent(progress);
    }, 100);

    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, [sessionId, config, isResultReady]);

  useEffect(() => {
    if (!sessionId || state !== 'GENERATING') return;

    const pollResult = async () => {
      try {
        const ready = await checkResultReady();
        if (ready && !isResultReady) {
          setIsResultReady(true);
          setState('READY');
          
          // Send maximize message ONLY if widget is minimized
          // Check if widget is minimized by checking if parent has minimized state
          window.parent.postMessage({ type: 'NOTIFY_RESULT_READY' }, '*');
          
          await waitingService.updateSessionStatus(sessionId, 'ready');
          
          trackEvent('waiting_ready', {
            elapsedSeconds: Math.floor((Date.now() - startTimeRef.current) / 1000)
          });

          if (onResultReady && !resultReadyCalledRef.current) {
            resultReadyCalledRef.current = true;
            onResultReady();
          }
        }
      } catch (err) {
        console.error('Error polling result status:', err);
      }
    };

    pollingIntervalRef.current = setInterval(pollResult, 2000);
    pollResult();

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [sessionId, state, checkResultReady, isResultReady, onResultReady, trackEvent]);

  useEffect(() => {
    if (state !== 'READY' || !config) return;

    softGateTimeoutRef.current = setTimeout(() => {
      setState('FINALIZING');
      if (sessionId) {
        waitingService.updateSessionStatus(sessionId, 'finalizing');
      }
    }, config.soft_gate_seconds * 1000);

    return () => {
      if (softGateTimeoutRef.current) {
        clearTimeout(softGateTimeoutRef.current);
      }
    };
  }, [state, config, sessionId]);

  const handleContinueClick = useCallback(() => {
    if (state === 'GENERATING') return;

    const elapsedSeconds = Math.floor((Date.now() - startTimeRef.current) / 1000);

    trackEvent('continue_clicked', {
      state,
      elapsedSeconds
    });

    trackEvent('waiting_completed', {
      state,
      elapsedSeconds,
      reelsViewed: currentReelIndex + 1,
      totalReels: reels.length
    });

    setState('COMPLETED');
    
    if (sessionId) {
      waitingService.updateSessionStatus(sessionId, 'completed');
    }
  }, [state, sessionId, trackEvent, currentReelIndex, reels.length]);

  const nextReel = useCallback(() => {
    if (currentReelIndex < reels.length - 1) {
      const newIndex = currentReelIndex + 1;
      setCurrentReelIndex(newIndex);
      
      if (reels[newIndex]) {
        trackEvent('reels_swipe', {
          reelId: reels[newIndex].id,
          direction: 'up'
        });
      }
    }
  }, [currentReelIndex, reels, trackEvent]);

  const prevReel = useCallback(() => {
    if (currentReelIndex > 0) {
      const newIndex = currentReelIndex - 1;
      setCurrentReelIndex(newIndex);
      
      if (reels[newIndex]) {
        trackEvent('reels_swipe', {
          reelId: reels[newIndex].id,
          direction: 'down'
        });
      }
    }
  }, [currentReelIndex, reels, trackEvent]);

  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
      if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
      if (softGateTimeoutRef.current) clearTimeout(softGateTimeoutRef.current);
    };
  }, []);

  return {
    state,
    sessionId,
    config,
    configId,
    progressPercent,
    elapsedSeconds,
    isResultReady,
    reels,
    currentReelIndex,
    startSession,
    handleContinueClick,
    nextReel,
    prevReel,
    trackEvent,
    isLoading,
    error,
    sessionMetadata
  };
}

export default useWaitingState;
